import React, { useEffect, useMemo, useState } from "react";
import api, { eur, fmtDate, STATUS_LABELS, STATUS_STYLES } from "../lib/api";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { Calendar } from "../components/ui/calendar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { CalendarDays, Package, Layers, Euro, CalendarClock, Truck, CheckCircle2, Plus } from "lucide-react";
import { toast } from "sonner";

const NEXT_STATUS = {
  nouvelle: { label: "Confirmer", to: "confirmee" },
  confirmee: { label: "En préparation", to: "en_preparation" },
  planifiee: { label: "En livraison", to: "en_livraison" },
  en_livraison: { label: "Marquer livrée", to: "livree" },
};

const iso = (d) => d.toISOString().slice(0, 10);

function StatCard({ icon: Icon, label, value, accent }) {
  return (
    <Card className="p-4">
      <div className="flex items-center gap-3">
        <div className={`flex h-10 w-10 items-center justify-center rounded-md ${accent}`}>
          <Icon className="h-5 w-5" />
        </div>
        <div>
          <p className="text-xs uppercase tracking-wider text-muted-foreground">{label}</p>
          <p className="tabular font-display text-2xl font-bold">{value}</p>
        </div>
      </div>
    </Card>
  );
}

function ScheduleDialog({ order, onDone }) {
  const [date, setDate] = useState(order.delivery_date ? new Date(order.delivery_date) : undefined);
  const [open, setOpen] = useState(false);
  const save = async () => {
    if (!date) return;
    await api.patch(`/orders/${order.id}/schedule`, { delivery_date: iso(date) });
    toast.success(`Livraison planifiée le ${fmtDate(iso(date))}`);
    setOpen(false);
    onDone();
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" data-testid={`schedule-btn-${order.id}`}>
          <CalendarClock className="mr-1 h-4 w-4" /> Planifier
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-fit">
        <DialogHeader>
          <DialogTitle>Planifier la livraison — {order.order_number}</DialogTitle>
          <DialogDescription>Choisissez une date de livraison pour {order.client_name}.</DialogDescription>
        </DialogHeader>
        <Calendar mode="single" selected={date} onSelect={setDate} className="rounded-md border" />
        <DialogFooter>
          <Button onClick={save} disabled={!date} data-testid="confirm-schedule-btn">Enregistrer la date</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ProductDialog({ product, onDone }) {
  const empty = { name: "", category: "Épicerie", unit: "carton", price: 0, stock: 0, description: "" };
  const [form, setForm] = useState(product || empty);
  const [open, setOpen] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const save = async () => {
    const payload = { ...form, price: parseFloat(form.price) || 0, stock: parseInt(form.stock) || 0 };
    if (product) await api.put(`/products/${product.id}`, payload);
    else await api.post("/products", payload);
    toast.success(product ? "Produit mis à jour" : "Produit ajouté");
    setOpen(false);
    onDone();
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {product ? (
          <Button size="sm" variant="ghost" data-testid={`edit-product-${product.id}`}>Modifier</Button>
        ) : (
          <Button size="sm" data-testid="add-product-btn"><Plus className="mr-1 h-4 w-4" /> Nouveau produit</Button>
        )}
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{product ? "Modifier le produit" : "Nouveau produit"}</DialogTitle>
          <DialogDescription>Renseignez les informations du produit du catalogue.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <Label>Nom</Label>
            <Input data-testid="pf-name" value={form.name} onChange={(e) => set("name", e.target.value)} />
          </div>
          <div>
            <Label>Catégorie</Label>
            <Input data-testid="pf-category" value={form.category} onChange={(e) => set("category", e.target.value)} />
          </div>
          <div>
            <Label>Unité</Label>
            <Input data-testid="pf-unit" value={form.unit} onChange={(e) => set("unit", e.target.value)} />
          </div>
          <div>
            <Label>Prix (€)</Label>
            <Input data-testid="pf-price" type="number" value={form.price} onChange={(e) => set("price", e.target.value)} />
          </div>
          <div>
            <Label>Stock</Label>
            <Input data-testid="pf-stock" type="number" value={form.stock} onChange={(e) => set("stock", e.target.value)} />
          </div>
        </div>
        <DialogFooter>
          <Button onClick={save} data-testid="save-product-btn">Enregistrer</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function UsineView() {
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [stats, setStats] = useState(null);
  const [statusFilter, setStatusFilter] = useState("tous");
  const [selectedDay, setSelectedDay] = useState(new Date());

  const load = () => {
    api.get("/orders").then((r) => setOrders(r.data)).catch(() => {});
    api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
    api.get("/stats").then((r) => setStats(r.data)).catch(() => {});
  };
  useEffect(load, []);

  const advance = async (o) => {
    const next = NEXT_STATUS[o.status];
    if (!next) return;
    await api.patch(`/orders/${o.id}/status`, { status: next.to });
    toast.success(`${o.order_number} → ${STATUS_LABELS[next.to]}`);
    load();
  };

  const filtered = statusFilter === "tous"
    ? orders.filter((o) => !["en_attente", "refusee"].includes(o.status))
    : orders.filter((o) => o.status === statusFilter);

  const scheduledDates = useMemo(() => {
    const map = {};
    orders.forEach((o) => {
      if (o.delivery_date) map[o.delivery_date] = (map[o.delivery_date] || 0) + 1;
    });
    return map;
  }, [orders]);

  const dayDeliveries = orders.filter((o) => o.delivery_date === iso(selectedDay));

  return (
    <div data-testid="usine-view">
      <div className="mb-6">
        <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Poste de pilotage</p>
        <h1 className="font-display text-3xl font-extrabold tracking-tight">Tableau de bord Usine</h1>
      </div>

      {stats && (
        <div className="mb-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
          <StatCard icon={Package} label="Commandes" value={stats.total_orders} accent="bg-primary/10 text-primary" />
          <StatCard icon={CalendarClock} label="À planifier" value={stats.to_schedule} accent="bg-secondary/15 text-secondary" />
          <StatCard icon={Layers} label="Produits" value={products.length} accent="bg-amber-500/10 text-amber-600" />
          <StatCard icon={Euro} label="CA cumulé" value={eur(stats.total_revenue)} accent="bg-primary/10 text-primary" />
        </div>
      )}

      <Tabs defaultValue="commandes">
        <TabsList data-testid="usine-tabs">
          <TabsTrigger value="commandes" data-testid="tab-commandes">Commandes</TabsTrigger>
          <TabsTrigger value="agenda" data-testid="tab-agenda">Agenda partagé</TabsTrigger>
          <TabsTrigger value="produits" data-testid="tab-produits">Produits</TabsTrigger>
        </TabsList>

        {/* Orders */}
        <TabsContent value="commandes" className="mt-4">
          <div className="mb-3 flex items-center gap-2">
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Filtrer</Label>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-48" data-testid="status-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="tous">Tous les statuts</SelectItem>
                {Object.entries(STATUS_LABELS).map(([k, v]) => (
                  <SelectItem key={k} value={k}>{v}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Card className="overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Commande</TableHead>
                  <TableHead>Distributeur</TableHead>
                  <TableHead>Articles</TableHead>
                  <TableHead>Total</TableHead>
                  <TableHead>Livraison</TableHead>
                  <TableHead>Statut</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((o) => (
                  <TableRow key={o.id} data-testid={`order-row-${o.id}`}>
                    <TableCell className="tabular font-medium">{o.order_number}</TableCell>
                    <TableCell>{o.client_name}<br /><span className="text-xs text-muted-foreground">{o.client_city}</span></TableCell>
                    <TableCell className="tabular">{o.items.length}</TableCell>
                    <TableCell className="tabular font-semibold">{eur(o.total)}</TableCell>
                    <TableCell>{fmtDate(o.delivery_date)}</TableCell>
                    <TableCell><Badge variant="outline" className={STATUS_STYLES[o.status]}>{STATUS_LABELS[o.status]}</Badge></TableCell>
                    <TableCell>
                      <div className="flex justify-end gap-2">
                        {NEXT_STATUS[o.status] && o.status !== "planifiee" && (
                          <Button size="sm" onClick={() => advance(o)} data-testid={`advance-btn-${o.id}`}>{NEXT_STATUS[o.status].label}</Button>
                        )}
                        {["nouvelle", "confirmee", "en_preparation", "planifiee"].includes(o.status) && (
                          <ScheduleDialog order={o} onDone={load} />
                        )}
                        {o.status === "planifiee" && (
                          <Button size="sm" onClick={() => advance(o)} data-testid={`advance-btn-${o.id}`}>En livraison</Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {filtered.length === 0 && (
                  <TableRow><TableCell colSpan={7} className="py-8 text-center text-muted-foreground">Aucune commande.</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        {/* Agenda */}
        <TabsContent value="agenda" className="mt-4">
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-[auto_1fr]">
            <Card className="p-4">
              <div className="mb-2 flex items-center gap-2">
                <CalendarDays className="h-4 w-4 text-primary" />
                <h3 className="font-display font-bold">Agenda des livraisons</h3>
              </div>
              <Calendar
                mode="single"
                selected={selectedDay}
                onSelect={(d) => d && setSelectedDay(d)}
                className="rounded-md"
                modifiers={{ scheduled: Object.keys(scheduledDates).map((d) => new Date(d + "T00:00:00")) }}
                modifiersClassNames={{ scheduled: "bg-primary/15 text-primary font-bold rounded-md" }}
              />
              <p className="mt-2 text-xs text-muted-foreground">Les jours en vert comportent des livraisons planifiées.</p>
            </Card>
            <Card className="p-4">
              <h3 className="font-display font-bold">Livraisons du {fmtDate(iso(selectedDay))}</h3>
              <div className="mt-3 space-y-2">
                {dayDeliveries.length === 0 && <p className="text-sm text-muted-foreground">Aucune livraison planifiée ce jour.</p>}
                {dayDeliveries.map((o) => (
                  <div key={o.id} data-testid={`agenda-item-${o.id}`} className="flex items-center justify-between rounded-lg border border-border p-3">
                    <div>
                      <p className="font-medium">{o.client_name} <span className="text-xs text-muted-foreground">· {o.client_city}</span></p>
                      <p className="tabular text-xs text-muted-foreground">{o.order_number} · {o.items.length} article(s)</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="tabular font-semibold">{eur(o.total)}</span>
                      <Badge variant="outline" className={STATUS_STYLES[o.status]}>{STATUS_LABELS[o.status]}</Badge>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          </div>
        </TabsContent>

        {/* Products */}
        <TabsContent value="produits" className="mt-4">
          <div className="mb-3 flex justify-end">
            <ProductDialog onDone={load} />
          </div>
          <Card className="overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Produit</TableHead>
                  <TableHead>Catégorie</TableHead>
                  <TableHead>Unité</TableHead>
                  <TableHead>Prix</TableHead>
                  <TableHead>Stock</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {products.map((p) => (
                  <TableRow key={p.id} data-testid={`product-row-${p.id}`}>
                    <TableCell className="font-medium">{p.name}</TableCell>
                    <TableCell>{p.category}</TableCell>
                    <TableCell>{p.unit}</TableCell>
                    <TableCell className="tabular">{eur(p.price)}</TableCell>
                    <TableCell>
                      <span className={`tabular font-semibold ${p.stock < 50 ? "text-secondary" : "text-primary"}`}>{p.stock}</span>
                    </TableCell>
                    <TableCell className="text-right"><ProductDialog product={p} onDone={load} /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
